/**
 * useOnePager.ts
 * Orchestration hook for the KPI One Pager V2.
 *
 * Responsibilities:
 * - Builds the API request via onePagerRequestMapper
 * - Calls onePagerApi for HTTP communication
 * - Maps the response via onePagerResponseMapper
 * - Manages loading, error, retry state
 * - Manages pagination state (page, size)
 * - Caches by request key to prevent duplicate fetches
 * - Reacts to Redux sort state changes via useEffect
 *
 * Does NOT contain:
 * - Business logic
 * - Request/response transformation (delegated to mappers)
 * - UI rendering concerns
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { GroupedRow, TableSortState } from '../constants/onePagerConstants';
import { selectSort } from '../../../store/slices/onePagerSlice';
import { buildOnePagerRequest } from '../utils/onePagerRequestMapper';
import { mapOnePagerResponse } from '../utils/onePagerResponseMapper';
import { fetchOnePager } from '../services/onePagerApi';
import { OnePagerFilters, ChartConfiguration, OnePagerRequest } from '../types/onePagerTypes';

// ─── Constants ────────────────────────────────────────────────────────────────

const DEFAULT_PAGE = 1;
const DEFAULT_SIZE = 25;

// ─── Hook Return Type ─────────────────────────────────────────────────────────

export interface UseOnePagerResult {
	/** Mapped data rows for the table */
	rows: GroupedRow[];
	/** Grand total row (from backend summary) */
	grandTotalRow: GroupedRow | null;
	/** Total number of rows available (for pagination) */
	totalRows: number;
	/** Total number of pages available */
	totalPages: number;
	/** Current page number (1-based) */
	page: number;
	/** Current page size */
	size: number;
	/** Whether a fetch is in progress */
	loading: boolean;
	/** Error message if the last fetch failed */
	error: string | null;
	/** Navigate to a specific page */
	setPage: (page: number) => void;
	/** Change the page size (resets to page 1) */
	setSize: (size: number) => void;
	/** Retry the last failed request */
	retry: () => void;
}

// ─── Hook Parameters ──────────────────────────────────────────────────────────

export interface UseOnePagerParams {
	readonly filters: OnePagerFilters;
	readonly chartConfiguration: ChartConfiguration;
	readonly enabled?: boolean;
}

// ─── Hook Implementation ──────────────────────────────────────────────────────

export function useOnePager(params: UseOnePagerParams): UseOnePagerResult {
	const { filters, chartConfiguration, enabled = true } = params;

	// ─── Redux state (single source of truth for sort) ──────────────────
	const sort: TableSortState = useSelector(selectSort);

	// ─── Local pagination state ─────────────────────────────────────────
	const [page, setPageState] = useState(DEFAULT_PAGE);
	const [size, setSizeState] = useState(DEFAULT_SIZE);

	// ─── Data state ─────────────────────────────────────────────────────
	const [rows, setRows] = useState<GroupedRow[]>([]);
	const [grandTotalRow, setGrandTotalRow] = useState<GroupedRow | null>(null);
	const [totalRows, setTotalRows] = useState(0);
	const [totalPages, setTotalPages] = useState(1);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// ─── Cache key to prevent duplicate fetches ─────────────────────────
	const lastRequestKeyRef = useRef<string>('');
	const abortControllerRef = useRef<AbortController | null>(null);

	// ─── Build the request (memoized) ───────────────────────────────────
	const request: OnePagerRequest = useMemo(
		() =>
			buildOnePagerRequest({
				chartConfiguration,
				filters,
				sort,
				page,
				size,
			}),
		[chartConfiguration, filters, sort, page, size]
	);

	// ─── Request cache key ──────────────────────────────────────────────
	const requestKey = useMemo(() => JSON.stringify(request), [request]);

	// ─── Fetch function ─────────────────────────────────────────────────
	const executeFetch = useCallback(
		async (force = false) => {
			if (!enabled) return;

			// Skip if request hasn't changed (cache hit) unless forced
			if (!force && requestKey === lastRequestKeyRef.current) return;

			// Abort any in-flight request
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}
			abortControllerRef.current = new AbortController();

			lastRequestKeyRef.current = requestKey;
			setLoading(true);
			setError(null);
			// Clear stale rows immediately so OnePagerTable shows its loading state
			// while new data is in-flight (grain changes, filter changes, page changes, etc.)
			setRows([]);
			setGrandTotalRow(null);
			setTotalRows(0);
			setTotalPages(1);

			try {
				const responseDTO = await fetchOnePager(request);
				const mapped = mapOnePagerResponse(responseDTO);

				setRows(mapped.rows);
				setGrandTotalRow(mapped.grandTotalRow);
				setTotalRows(mapped.totalRows);
				setTotalPages(mapped.totalPages);
			} catch (err: any) {
				// Don't set error for aborted requests
				if (err?.name === 'AbortError') return;

				const message =
					err?.message || err?.response?.data?.message || 'Failed to load One Pager data';
				setError(message);
				setRows([]);
				setGrandTotalRow(null);
				setTotalRows(0);
			} finally {
				setLoading(false);
			}
		},
		[enabled, request, requestKey]
	);

	// ─── Auto-fetch when request changes ────────────────────────────────
	useEffect(() => {
		executeFetch();
	}, [executeFetch]);

	// ─── Reset page to 1 when filters/sort/grains change ───────────────
	const prevFiltersKeyRef = useRef<string>('');
	const filtersKey = useMemo(
		() => JSON.stringify({ filters, chartConfiguration, sort }),
		[filters, chartConfiguration, sort]
	);

	useEffect(() => {
		if (prevFiltersKeyRef.current && prevFiltersKeyRef.current !== filtersKey) {
			setPageState(DEFAULT_PAGE);
		}
		prevFiltersKeyRef.current = filtersKey;
	}, [filtersKey]);

	// ─── Public pagination setters ──────────────────────────────────────
	const setPage = useCallback((newPage: number) => {
		setPageState(Math.max(1, newPage));
	}, []);

	const setSize = useCallback((newSize: number) => {
		setSizeState(newSize);
		setPageState(DEFAULT_PAGE); // Reset to page 1 on size change
	}, []);

	// ─── Retry ──────────────────────────────────────────────────────────
	const retry = useCallback(() => {
		executeFetch(true);
	}, [executeFetch]);

	// ─── Cleanup on unmount ─────────────────────────────────────────────
	useEffect(() => {
		return () => {
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}
		};
	}, []);

	return {
		rows,
		grandTotalRow,
		totalRows,
		totalPages,
		page,
		size,
		loading,
		error,
		setPage,
		setSize,
		retry,
	};
}
